import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisionEditor = ({ data, onChange, token }) => {
  const [uploading, setUploading] = useState(false);

  const handleChange = (section, field, value) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [field]: value
      }
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      onChange({ ...data, image: imageUrl });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Vision upload error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleConceptFeatureChange = (index, value) => {
    const newFeatures = [...data.concept.features];
    newFeatures[index] = value;
    onChange({
      ...data,
      concept: {
        ...data.concept,
        features: newFeatures
      }
    });
  };

  const addConceptFeature = () => {
    onChange({
      ...data,
      concept: {
        ...data.concept,
        features: [...data.concept.features, '']
      }
    });
  };

  const removeConceptFeature = (index) => {
    const newFeatures = data.concept.features.filter((_, i) => i !== index);
    onChange({
      ...data,
      concept: {
        ...data.concept,
        features: newFeatures
      }
    });
  };

  const handleFactChange = (index, field, value) => {
    const newFacts = [...data.fastFacts.facts];
    newFacts[index] = { ...newFacts[index], [field]: value };
    onChange({
      ...data,
      fastFacts: {
        ...data.fastFacts,
        facts: newFacts
      }
    });
  };

  const addFact = () => {
    onChange({
      ...data,
      fastFacts: {
        ...data.fastFacts,
        facts: [...data.fastFacts.facts, { label: '', value: '' }]
      }
    });
  };

  const removeFact = (index) => {
    const newFacts = data.fastFacts.facts.filter((_, i) => i !== index);
    onChange({
      ...data,
      fastFacts: {
        ...data.fastFacts,
        facts: newFacts
      }
    });
  };

  return (
    <div className="bg-white rounded-lg p-6 space-y-8">
      <h2 className="text-2xl font-bold text-slate-900">Vision Section</h2>
      
      {/* Section Title */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Section Title</label>
        <Input value={data.title} onChange={(e) => onChange({ ...data, title: e.target.value })} />
      </div>

      {/* Concept Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Concept</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
            <Input value={data.concept.title} onChange={(e) => handleChange('concept', 'title', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <Textarea value={data.concept.description} onChange={(e) => handleChange('concept', 'description', e.target.value)} rows={4} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Features</label>
            <div className="space-y-2">
              {data.concept.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => handleConceptFeatureChange(index, e.target.value)}
                    placeholder="Feature description"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeConceptFeature(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addConceptFeature}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Feature
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Positioning Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Positioning</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
            <Input value={data.positioning.title} onChange={(e) => handleChange('positioning', 'title', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Target Market</label>
            <Textarea 
              value={data.positioning.target} 
              onChange={(e) => handleChange('positioning', 'target', e.target.value)} 
              rows={2}
              placeholder="Describe your target market"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Unique Value</label>
            <Textarea 
              value={data.positioning.unique} 
              onChange={(e) => handleChange('positioning', 'unique', e.target.value)} 
              rows={2}
              placeholder="What makes you unique?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Market Segment</label>
            <Textarea 
              value={data.positioning.market} 
              onChange={(e) => handleChange('positioning', 'market', e.target.value)} 
              rows={2}
              placeholder="Describe your market segment"
            />
          </div>
        </div>
      </div>

      {/* Fast Facts Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Fast Facts</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Section Title</label>
            <Input 
              value={data.fastFacts.title} 
              onChange={(e) => onChange({ ...data, fastFacts: { ...data.fastFacts, title: e.target.value } })} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Facts</label>
            <div className="space-y-3">
              {data.fastFacts.facts.map((fact, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input
                      value={fact.label}
                      onChange={(e) => handleFactChange(index, 'label', e.target.value)}
                      placeholder="Label (e.g., Location)"
                    />
                    <Input
                      value={fact.value}
                      onChange={(e) => handleFactChange(index, 'value', e.target.value)}
                      placeholder="Value (e.g., Alentejo, Portugal)"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeFact(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addFact}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Fast Fact
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Vision Image */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Vision Image</h3>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Image URL</label>
          <div className="flex items-center gap-4">
            <Input 
              value={data.image} 
              onChange={(e) => onChange({ ...data, image: e.target.value })} 
              placeholder="https://..."
              className="flex-1"
            />
            <label htmlFor="vision-image-upload">
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => document.getElementById('vision-image-upload').click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </label>
            <input
              id="vision-image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          {data.image && (
            <div className="mt-4">
              <img src={data.image} alt="Vision preview" className="w-full h-48 object-cover rounded-lg" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisionEditor;
